import type { InfraEnvironment, InfraNode } from "@/lib/api";

export type InfraNodeKind = "base" | "added" | "dedicated";

export const INFRA_NODE_KIND_LABELS: Record<InfraNodeKind, string> = {
	base: "常設",
	added: "増設",
	dedicated: "専有",
};

/** 専有ノードは nodegroup が `-gpu` / `-cpu` で終わる。それ以外で Karpenter が起動したものが増設ノード。 */
export function infraNodeKind(node: InfraNode): InfraNodeKind {
	if (node.nodeGroup.endsWith("-gpu") || node.nodeGroup.endsWith("-cpu")) return "dedicated";
	return node.karpenterNodePool === null ? "base" : "added";
}

export interface InfraNodeGroup {
	node: InfraNode;
	environments: InfraEnvironment[];
}

/**
 * 環境をノードごとにまとめる。ノードが未割り当て、または一覧に無いノード (env ノード以外) に載っている
 * 環境は unplaced に入れる。
 */
export function groupEnvironmentsByNode(
	nodes: readonly InfraNode[],
	environments: readonly InfraEnvironment[],
): { groups: InfraNodeGroup[]; unplaced: InfraEnvironment[] } {
	const groups = nodes.map((node) => ({ node, environments: [] as InfraEnvironment[] }));
	const byName = new Map(groups.map((group) => [group.node.name, group]));
	const unplaced: InfraEnvironment[] = [];
	for (const environment of environments) {
		const group = environment.nodeName === null ? undefined : byName.get(environment.nodeName);
		if (group) group.environments.push(environment);
		else unplaced.push(environment);
	}
	return { groups, unplaced };
}

export interface InfraOrganization {
	id: string;
	name: string;
	environmentCount: number;
}

/** 環境の載っている組織を名前順に並べる。DB に環境行が無く組織の分からない環境は数えない。 */
export function infraOrganizations(environments: readonly InfraEnvironment[]): InfraOrganization[] {
	const byId = new Map<string, InfraOrganization>();
	for (const environment of environments) {
		if (environment.organizationId === null) continue;
		const organization = byId.get(environment.organizationId);
		if (organization) organization.environmentCount++;
		else
			byId.set(environment.organizationId, {
				id: environment.organizationId,
				name: environment.organizationName ?? environment.organizationId,
				environmentCount: 1,
			});
	}
	return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "ja"));
}

/**
 * 選んだ組織の環境と、それが載っているノードだけを残す。組織を選んでいなければ (null) 全件を返す。
 */
export function filterPlacementByOrganization(
	placement: { groups: readonly InfraNodeGroup[]; unplaced: readonly InfraEnvironment[] },
	organizationId: string | null,
): { groups: InfraNodeGroup[]; unplaced: InfraEnvironment[] } {
	if (organizationId === null)
		return { groups: [...placement.groups], unplaced: [...placement.unplaced] };
	const belongs = (environment: InfraEnvironment) => environment.organizationId === organizationId;
	return {
		groups: placement.groups
			.map((group) => ({ ...group, environments: group.environments.filter(belongs) }))
			.filter((group) => group.environments.length > 0),
		unplaced: placement.unplaced.filter(belongs),
	};
}

export const INFRA_NODE_KINDS: readonly InfraNodeKind[] = ["base", "added", "dedicated"];

/** 常設 → 増設 → 専有の順に並べる。同じ種別の中では元の順を保つ。 */
export function sortNodeGroupsByKind(groups: readonly InfraNodeGroup[]): InfraNodeGroup[] {
	return [...groups].sort(
		(a, b) =>
			INFRA_NODE_KINDS.indexOf(infraNodeKind(a.node)) -
			INFRA_NODE_KINDS.indexOf(infraNodeKind(b.node)),
	);
}

/** 選んだ種別のノードだけを残す。何も選んでいなければ全件を返す。 */
export function filterNodeGroupsByKind(
	groups: readonly InfraNodeGroup[],
	selectedKinds: ReadonlySet<InfraNodeKind>,
): InfraNodeGroup[] {
	if (selectedKinds.size === 0) return [...groups];
	return groups.filter((group) => selectedKinds.has(infraNodeKind(group.node)));
}

/** 絞り込みの種類を選んでいなければ選び、選んでいれば外す。 */
export function toggleKind<T>(selectedKinds: ReadonlySet<T>, kind: T): Set<T> {
	const next = new Set(selectedKinds);
	if (next.has(kind)) next.delete(kind);
	else next.add(kind);
	return next;
}

/** 状態の色。稼働中などの動いている状態は success、移り変わりの途中は warning (DESIGN.md)。 */
export type InfraStatusTone = "success" | "warning" | "danger";

export function infraEnvironmentStatus(environment: InfraEnvironment): {
	label: string;
	tone: InfraStatusTone;
} {
	if (environment.terminating) return { label: "終了中", tone: "warning" };
	if (environment.nodeName === null) return { label: "割り当て待ち", tone: "warning" };
	if (environment.phase === "Running" && environment.ready)
		return { label: "稼働中", tone: "success" };
	return { label: "起動中", tone: "warning" };
}

/**
 * Karpenter のノードが今の AMI で動いているか (更新済み) か、入れ替え待ちか。入れ替え待ちの一覧を読めなければ、
 * 「更新済み」と誤って見せないよう不明として出す。Karpenter 以外のノード (ローカル kind) は入れ替えの対象ではないので null。
 */
export function infraNodeAmiStatus(
	node: InfraNode,
): { label: string; tone: InfraStatusTone } | null {
	if (node.karpenterNodePool === null) return null;
	if (node.isOutdated == null) return { label: "入れ替え状況不明", tone: "danger" };
	return node.isOutdated
		? { label: "入れ替え待ち", tone: "warning" }
		: { label: "更新済み", tone: "success" };
}

/** request ÷ allocatable を 0〜1 に収める。allocatable が無ければ 0。 */
export function usageRatio(requested: number, allocatable: number): number {
	if (allocatable <= 0) return 0;
	return Math.min(Math.max(requested / allocatable, 0), 1);
}

export function formatMilliCpu(milliCpu: number): string {
	return `${(milliCpu / 1000).toFixed(1)} vCPU`;
}

/** 起動からの経過時間を「3分」「2時間5分」「1日3時間」のように表す。 */
export function formatAge(fromIso: string, now: Date): string {
	const minutes = Math.max(Math.floor((now.getTime() - new Date(fromIso).getTime()) / 60_000), 0);
	if (minutes < 60) return `${minutes}分`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}時間${minutes % 60}分`;
	return `${Math.floor(hours / 24)}日${hours % 24}時間`;
}
